import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import TextInput from 'components/Inputs/TextInput'
import BusinessServiceList from 'components/Lists/BusinessServiceList'
import CutileiServicesConfigModal from 'pages/Modals/CutileiServicesConfigModal'
// import BusinessServicesModal from 'pages/Modals/ServicesModal/Business'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import AddServiceModal from 'pages/Modals/AddServiceModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Container, Row } from '../styles'

export default function EditServices () {
  const [loadingData, setLoadingData] = useState (true)
  const [loadingServices, setLoadingServices] = useState (true)
  const [services, setServices] = useState ([])
  const [categories, setCategories] = useState ([])
  const [professionals, setProfessionals] = useState ([])
  const [products, setProducts] = useState ([])
  const [weekdays, setWeekdays] = useState ([])
  const [category, setCategory] = useState (null)
  const [name, setName] = useState ('')
  const [orderBy, setOrderBy] = useState ('')
  // const [showServicesModal, setShowServicesModal] = useState (false)
  const [showConfigCutileiServicesModal, setShowConfigCutileiServicesModal] = useState (false)
  const [showAddServiceModal, setShowAddServiceModal] = useState (false)
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()
  const navigate = useNavigate ()
  const compareServices = (a, b) => a.number - b.number

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const cutileiServices = useMemo (() => (
    services.filter (service => service.cutilei_service).sort (compareServices)
  ), [services])

  const isCutileiServicesConfigured = useMemo (() => (
    cutileiServices.every (service => service.comission_percentage > 0 && service.duration > 0)
  ), [cutileiServices])

  useEffect (() => {
    getData ()
  }, [])

  useEffect (() => {
    getServices ()
  }, [category, name])

  useEffect (() => {
    if (!loadingData && !loadingServices && !isCutileiServicesConfigured) {
      setShowConfigCutileiServicesModal (true)
    }
  }, [loadingData, loadingServices, isCutileiServicesConfigured])

  const getData = async () => {
    try {
      setLoadingData (true)

      const [
        { data: weekdays },
        { data: categories },
        { data: products },
        { data: professionals }
      ] = await Promise.all ([
        cutileiApi.get ('/weekdays'),
        cutileiApi.get ('/service_categories', requestConfig),
        cutileiApi.get (`/businesses/${businessId}/products?used_in_service=true`, requestConfig),
        cutileiApi.get (
          `/businesses/${businessId}/professionals?access_level=service&status=active&deleted=false`,
          requestConfig
        )
      ])
      
      setWeekdays (weekdays)
      setProfessionals (professionals)
      setCategories (categories.map (category => ({
        label: category.name,
        value: category.id
      })))
      setProducts (products.map (product => ({
        label: product.name,
        value: product
      })))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingData (false)
    }
  }

  const getServices = async () => {
    let filters = ''
    if (name) filters += `&name=${encodeURIComponent (name)}`
    if (category) filters += `&category_id=${category.value}`
    if (orderBy) filters += `&order_by=${orderBy}`

    try {
      setLoadingServices (true)

      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/services?status=active${filters}`, requestConfig
      )

      setServices (services)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingServices (false)
    }
  }

  const handleAddService = service => {
    const serviceExists = services.some (s => s.id === service.id)
    if (serviceExists) {
      setServices (services.map (s => s.id === service.id ? service : s))
    } else {
      setServices ([...services, service].sort (compareServices))
    }
  }

  const handleCutileiServicesConfigured = updatedServices => {
    const updatedServicesMap = new Map (updatedServices.map (s => [s.id, s]))
    
    setServices (services.map (s => updatedServicesMap.get (s.id) || s))
    returnToHome ()
  }

  // const toggleServicesModal = () => setShowServicesModal (!showServicesModal)
  const toggleAddServiceModal = () => setShowAddServiceModal (!showAddServiceModal)
  const toggleConfigCutileiServicesModal = () => {
    setShowConfigCutileiServicesModal (!showConfigCutileiServicesModal)
  }

  const returnToHome = () => navigate ('/business', { replace: true })

  return (
    <Container>
      <Row style={{gap: 10, width: 600}}>
        <Select
          name='category'
          placeholder='Categoria...'
          value={category}
          options={categories}
          isClearable={true}
          onChange={(_, option) => setCategory (option)}
        />
        <TextInput
          value={name}
          placeholder='Pesquisar por nome'
          isClearable={true}
          onChange={e => setName (e.target.value)}
          onClear={() => setName ('')}
          inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
        />
      </Row>
      {AuthService.hasPermission ('business_config.services', 'edit') && (
        <Row style={{gap: 10}}>
          {/* <Button onClick={toggleServicesModal}>
            Adicionar serviços
          </Button> */}
          <SecondaryButton
            label='Cadastrar serviço'
            onClick={toggleAddServiceModal}
            width={160}
          />
          {/* <BusinessServicesModal
            visible={showServicesModal}
            businessProducts={products}
            professionals={professionals}
            onConfirm={addedServices => setServices (
              [...services, ...addedServices].sort (compareServices)
            )}
            onClose={toggleServicesModal}
          /> */}
          <AddServiceModal
            visible={showAddServiceModal}
            serviceCategories={categories}
            professionals={professionals}
            weekdays={weekdays}
            businessProducts={products}
            onConfirm={handleAddService}
            onClose={toggleAddServiceModal}
          />
        </Row>
      )}
      {loadingData || loadingServices ? <Loading/> : (
        <BusinessServiceList
          services={services}
          weekdays={weekdays}
          businessProducts={products}
          serviceCategories={categories}
          onEdit={service => setServices (services.map (s => s.id === service.id ? service : s))}
          onDelete={deletedService => setServices (services.filter (s => s.id !== deletedService.id))}
        />
      )}
      <CutileiServicesConfigModal
        visible={showConfigCutileiServicesModal}
        services={cutileiServices}
        businessProducts={products}
        onConfirm={handleCutileiServicesConfigured}
        onClose={toggleConfigCutileiServicesModal}
      />
    </Container>
  )
}
